import { useCallback, useEffect, useMemo, useState } from 'react'

/**
 * How this administrator has arranged their own dashboard.
 *
 * Deliberately NOT platform configuration: which panels one administrator wants
 * in front of them is a personal display preference, not a decision about the
 * deployment, so it is not published, not audited and not imposed on anyone
 * else. It is stored per browser and stated as such on the dialog.
 *
 * Order is stored as ids rather than positions, and hidden ids are stored
 * separately, so a widget added or removed in a later release resolves cleanly:
 * unknown ids are dropped and new ones appear at the end rather than the layout
 * being discarded.
 */
export interface WidgetLayout {
  order: string[]
  hidden: string[]
}

const STORAGE_KEY = 'campuzon.admin.dashboard'

function read(): WidgetLayout {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<WidgetLayout>
      if (Array.isArray(parsed.order) && Array.isArray(parsed.hidden)) {
        return {
          order: parsed.order.filter((id): id is string => typeof id === 'string'),
          hidden: parsed.hidden.filter((id): id is string => typeof id === 'string'),
        }
      }
    }
  } catch {
    // A stored preference that cannot be read is not a reason to fail to render
    // the dashboard. Fall back to the shipped arrangement.
  }
  return { order: [], hidden: [] }
}

export interface WidgetLayoutController {
  /** Ids in presentation order, hidden ones excluded. */
  visible: string[]
  /** Every id in presentation order, including hidden ones (for the editor). */
  ordered: string[]
  isHidden: (id: string) => boolean
  toggle: (id: string) => void
  move: (id: string, direction: -1 | 1) => void
  reset: () => void
  /** True when this browser holds an arrangement of its own. */
  customised: boolean
}

export function useWidgetLayout(available: string[]): WidgetLayoutController {
  const [layout, setLayout] = useState<WidgetLayout>(() => read())

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(layout))
    } catch {
      // Honour the arrangement for this session rather than snapping back.
    }
  }, [layout])

  // Stored order first (filtered to what still exists), then anything the
  // stored order does not mention — so a newly shipped widget is shown rather
  // than silently withheld because it was not in an old preference.
  const ordered = useMemo(() => {
    const known = layout.order.filter((id) => available.includes(id))
    return [...known, ...available.filter((id) => !known.includes(id))]
  }, [layout.order, available])

  const isHidden = useCallback((id: string) => layout.hidden.includes(id), [layout.hidden])

  const toggle = useCallback((id: string) => {
    setLayout((current) => ({
      ...current,
      hidden: current.hidden.includes(id)
        ? current.hidden.filter((item) => item !== id)
        : [...current.hidden, id],
    }))
  }, [])

  const move = useCallback(
    (id: string, direction: -1 | 1) => {
      setLayout((current) => {
        const known = current.order.filter((item) => available.includes(item))
        const list = [...known, ...available.filter((item) => !known.includes(item))]
        const index = list.indexOf(id)
        const next = index + direction
        if (index < 0 || next < 0 || next >= list.length) return current
        const reordered = [...list]
        reordered[index] = list[next]
        reordered[next] = id
        return { ...current, order: reordered }
      })
    },
    [available],
  )

  const reset = useCallback(() => setLayout({ order: [], hidden: [] }), [])

  return {
    visible: ordered.filter((id) => !layout.hidden.includes(id)),
    ordered,
    isHidden,
    toggle,
    move,
    reset,
    customised: layout.order.length > 0 || layout.hidden.length > 0,
  }
}
